/**
 * Convert an ISO 3166-1 alpha-2 country code to its flag emoji.
 * Each letter is offset to the Regional Indicator Symbol range (U+1F1E6..U+1F1FF).
 */
export function countryCodeToFlag(code: string): string {
  return [...code.toUpperCase()].map(c => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65)).join('');
}

export interface CountryOption {
  code: string;
  label: string;
}

// Francophone / priority countries shown first, then all others alphabetically
const PRIORITY_CODES = [
  'FR', 'BE', 'CH', 'LU', 'MC',
  'MA', 'TN', 'DZ', 'SN', 'CI', 'CM', 'CG', 'TG', 'MG', 'GA', 'MU',
  'CA', 'LB', 'ES', 'DE', 'IT', 'AD',
];

const ALL_COUNTRIES: CountryOption[] = [
  { code: 'AF', label: 'Afghanistan' },
  { code: 'ZA', label: 'Afrique du Sud' },
  { code: 'AL', label: 'Albanie' },
  { code: 'DZ', label: 'Algérie' },
  { code: 'DE', label: 'Allemagne' },
  { code: 'AD', label: 'Andorre' },
  { code: 'AO', label: 'Angola' },
  { code: 'AG', label: 'Antigua-et-Barbuda' },
  { code: 'SA', label: 'Arabie saoudite' },
  { code: 'AR', label: 'Argentine' },
  { code: 'AM', label: 'Arménie' },
  { code: 'AU', label: 'Australie' },
  { code: 'AT', label: 'Autriche' },
  { code: 'AZ', label: 'Azerbaïdjan' },
  { code: 'BS', label: 'Bahamas' },
  { code: 'BH', label: 'Bahreïn' },
  { code: 'BD', label: 'Bangladesh' },
  { code: 'BB', label: 'Barbade' },
  { code: 'BE', label: 'Belgique' },
  { code: 'BZ', label: 'Belize' },
  { code: 'BJ', label: 'Bénin' },
  { code: 'BT', label: 'Bhoutan' },
  { code: 'BY', label: 'Biélorussie' },
  { code: 'BO', label: 'Bolivie' },
  { code: 'BA', label: 'Bosnie-Herzégovine' },
  { code: 'BW', label: 'Botswana' },
  { code: 'BR', label: 'Brésil' },
  { code: 'BN', label: 'Brunei' },
  { code: 'BG', label: 'Bulgarie' },
  { code: 'BF', label: 'Burkina Faso' },
  { code: 'BI', label: 'Burundi' },
  { code: 'KH', label: 'Cambodge' },
  { code: 'CM', label: 'Cameroun' },
  { code: 'CA', label: 'Canada' },
  { code: 'CV', label: 'Cap-Vert' },
  { code: 'CF', label: 'Centrafrique' },
  { code: 'CL', label: 'Chili' },
  { code: 'CN', label: 'Chine' },
  { code: 'CY', label: 'Chypre' },
  { code: 'CO', label: 'Colombie' },
  { code: 'KM', label: 'Comores' },
  { code: 'CG', label: 'Congo' },
  { code: 'CD', label: 'Congo (RDC)' },
  { code: 'KR', label: 'Corée du Sud' },
  { code: 'KP', label: 'Corée du Nord' },
  { code: 'CR', label: 'Costa Rica' },
  { code: 'CI', label: "Côte d'Ivoire" },
  { code: 'HR', label: 'Croatie' },
  { code: 'CU', label: 'Cuba' },
  { code: 'DK', label: 'Danemark' },
  { code: 'DJ', label: 'Djibouti' },
  { code: 'DM', label: 'Dominique' },
  { code: 'EG', label: 'Égypte' },
  { code: 'AE', label: 'Émirats arabes unis' },
  { code: 'EC', label: 'Équateur' },
  { code: 'ER', label: 'Érythrée' },
  { code: 'ES', label: 'Espagne' },
  { code: 'EE', label: 'Estonie' },
  { code: 'SZ', label: 'Eswatini' },
  { code: 'US', label: 'États-Unis' },
  { code: 'ET', label: 'Éthiopie' },
  { code: 'FJ', label: 'Fidji' },
  { code: 'FI', label: 'Finlande' },
  { code: 'FR', label: 'France' },
  { code: 'GA', label: 'Gabon' },
  { code: 'GM', label: 'Gambie' },
  { code: 'GE', label: 'Géorgie' },
  { code: 'GH', label: 'Ghana' },
  { code: 'GR', label: 'Grèce' },
  { code: 'GD', label: 'Grenade' },
  { code: 'GT', label: 'Guatemala' },
  { code: 'GN', label: 'Guinée' },
  { code: 'GQ', label: 'Guinée équatoriale' },
  { code: 'GW', label: 'Guinée-Bissau' },
  { code: 'GY', label: 'Guyana' },
  { code: 'HT', label: 'Haïti' },
  { code: 'HN', label: 'Honduras' },
  { code: 'HU', label: 'Hongrie' },
  { code: 'IN', label: 'Inde' },
  { code: 'ID', label: 'Indonésie' },
  { code: 'IQ', label: 'Irak' },
  { code: 'IR', label: 'Iran' },
  { code: 'IE', label: 'Irlande' },
  { code: 'IS', label: 'Islande' },
  { code: 'IL', label: 'Israël' },
  { code: 'IT', label: 'Italie' },
  { code: 'JM', label: 'Jamaïque' },
  { code: 'JP', label: 'Japon' },
  { code: 'JO', label: 'Jordanie' },
  { code: 'KZ', label: 'Kazakhstan' },
  { code: 'KE', label: 'Kenya' },
  { code: 'KG', label: 'Kirghizistan' },
  { code: 'KI', label: 'Kiribati' },
  { code: 'KW', label: 'Koweït' },
  { code: 'LA', label: 'Laos' },
  { code: 'LS', label: 'Lesotho' },
  { code: 'LV', label: 'Lettonie' },
  { code: 'LB', label: 'Liban' },
  { code: 'LR', label: 'Liberia' },
  { code: 'LY', label: 'Libye' },
  { code: 'LI', label: 'Liechtenstein' },
  { code: 'LT', label: 'Lituanie' },
  { code: 'LU', label: 'Luxembourg' },
  { code: 'MK', label: 'Macédoine du Nord' },
  { code: 'MG', label: 'Madagascar' },
  { code: 'MY', label: 'Malaisie' },
  { code: 'MW', label: 'Malawi' },
  { code: 'MV', label: 'Maldives' },
  { code: 'ML', label: 'Mali' },
  { code: 'MT', label: 'Malte' },
  { code: 'MA', label: 'Maroc' },
  { code: 'MU', label: 'Maurice' },
  { code: 'MR', label: 'Mauritanie' },
  { code: 'MX', label: 'Mexique' },
  { code: 'FM', label: 'Micronésie' },
  { code: 'MD', label: 'Moldavie' },
  { code: 'MC', label: 'Monaco' },
  { code: 'MN', label: 'Mongolie' },
  { code: 'ME', label: 'Monténégro' },
  { code: 'MZ', label: 'Mozambique' },
  { code: 'MM', label: 'Myanmar' },
  { code: 'NA', label: 'Namibie' },
  { code: 'NR', label: 'Nauru' },
  { code: 'NP', label: 'Népal' },
  { code: 'NI', label: 'Nicaragua' },
  { code: 'NE', label: 'Niger' },
  { code: 'NG', label: 'Nigeria' },
  { code: 'NO', label: 'Norvège' },
  { code: 'NZ', label: 'Nouvelle-Zélande' },
  { code: 'OM', label: 'Oman' },
  { code: 'UG', label: 'Ouganda' },
  { code: 'UZ', label: 'Ouzbékistan' },
  { code: 'PK', label: 'Pakistan' },
  { code: 'PW', label: 'Palaos' },
  { code: 'PS', label: 'Palestine' },
  { code: 'PA', label: 'Panama' },
  { code: 'PG', label: 'Papouasie-Nouvelle-Guinée' },
  { code: 'PY', label: 'Paraguay' },
  { code: 'NL', label: 'Pays-Bas' },
  { code: 'PE', label: 'Pérou' },
  { code: 'PH', label: 'Philippines' },
  { code: 'PL', label: 'Pologne' },
  { code: 'PT', label: 'Portugal' },
  { code: 'QA', label: 'Qatar' },
  { code: 'DO', label: 'République dominicaine' },
  { code: 'RO', label: 'Roumanie' },
  { code: 'GB', label: 'Royaume-Uni' },
  { code: 'RU', label: 'Russie' },
  { code: 'RW', label: 'Rwanda' },
  { code: 'KN', label: 'Saint-Kitts-et-Nevis' },
  { code: 'LC', label: 'Sainte-Lucie' },
  { code: 'VC', label: 'Saint-Vincent-et-les-Grenadines' },
  { code: 'SB', label: 'Salomon' },
  { code: 'SV', label: 'Salvador' },
  { code: 'WS', label: 'Samoa' },
  { code: 'ST', label: 'Sao Tomé-et-Principe' },
  { code: 'SN', label: 'Sénégal' },
  { code: 'RS', label: 'Serbie' },
  { code: 'SC', label: 'Seychelles' },
  { code: 'SL', label: 'Sierra Leone' },
  { code: 'SG', label: 'Singapour' },
  { code: 'SK', label: 'Slovaquie' },
  { code: 'SI', label: 'Slovénie' },
  { code: 'SO', label: 'Somalie' },
  { code: 'SD', label: 'Soudan' },
  { code: 'SS', label: 'Soudan du Sud' },
  { code: 'LK', label: 'Sri Lanka' },
  { code: 'SE', label: 'Suède' },
  { code: 'CH', label: 'Suisse' },
  { code: 'SR', label: 'Suriname' },
  { code: 'SY', label: 'Syrie' },
  { code: 'TJ', label: 'Tadjikistan' },
  { code: 'TZ', label: 'Tanzanie' },
  { code: 'TD', label: 'Tchad' },
  { code: 'CZ', label: 'Tchéquie' },
  { code: 'TH', label: 'Thaïlande' },
  { code: 'TL', label: 'Timor oriental' },
  { code: 'TG', label: 'Togo' },
  { code: 'TO', label: 'Tonga' },
  { code: 'TT', label: 'Trinité-et-Tobago' },
  { code: 'TN', label: 'Tunisie' },
  { code: 'TM', label: 'Turkménistan' },
  { code: 'TR', label: 'Turquie' },
  { code: 'TV', label: 'Tuvalu' },
  { code: 'UA', label: 'Ukraine' },
  { code: 'UY', label: 'Uruguay' },
  { code: 'VU', label: 'Vanuatu' },
  { code: 'VA', label: 'Vatican' },
  { code: 'VE', label: 'Venezuela' },
  { code: 'VN', label: 'Viêt Nam' },
  { code: 'YE', label: 'Yémen' },
  { code: 'ZM', label: 'Zambie' },
  { code: 'ZW', label: 'Zimbabwe' },
];

// Build the final list: priority countries first (as a separator group), then all alphabetically
const prioritySet = new Set(PRIORITY_CODES);
const priorityCountries = PRIORITY_CODES.map(code => ALL_COUNTRIES.find(c => c.code === code)!).filter(Boolean);
const otherCountries = ALL_COUNTRIES.filter(c => !prioritySet.has(c.code));

export const COUNTRY_OPTIONS = priorityCountries;
export const COUNTRY_OPTIONS_ALL = ALL_COUNTRIES;
export const COUNTRY_OPTIONS_OTHER = otherCountries;

/**
 * Get the full sorted list: priority countries first, then the rest alphabetically.
 * Use this for <Select> dropdowns.
 */
export function getCountryOptions(): { priority: CountryOption[]; others: CountryOption[] } {
  return { priority: priorityCountries, others: otherCountries };
}

/**
 * Find a country by its code.
 */
export function getCountryByCode(code: string): CountryOption | undefined {
  return ALL_COUNTRIES.find(c => c.code === code);
}
